"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DRAFT_ONLY_TEST_MASTER_FIELDS = exports.updateTestMasterSchema = exports.createTestMasterSchema = exports.testMasterShape = void 0;
const zod_1 = require("zod");
exports.testMasterShape = {
    testCode: zod_1.z.string().min(1).max(30),
    name: zod_1.z.string().min(1).max(255),
    sampleTypeId: zod_1.z.string().uuid().optional(),
    methodId: zod_1.z.string().uuid().optional(),
    unitId: zod_1.z.string().uuid().optional(),
    referenceRangeLow: zod_1.z.number().optional(),
    referenceRangeHigh: zod_1.z.number().optional(),
    referenceRangeText: zod_1.z.string().optional(),
    turnaroundMinutes: zod_1.z.number().int().positive().optional(),
    /**
     * ECM — the `ChargeMaster` this test bills as. The column has existed since
     * Phase 4 but was absent from this schema, so `validateRequest` stripped it
     * from every request and it could never be set through the API: exactly the
     * failure Phase 1 found on six other ECM columns.
     *
     * Once set, the test prices through `RateResolutionService` like everything
     * else. The legacy `price` column this replaced has been dropped.
     */
    chargeId: zod_1.z.string().uuid().nullish(),
    /**
     * Test Builder (Gate 2) — these ADR-087 `LabTest` columns existed on the
     * model since Phase 2/3 but were never added to this schema, so a caller
     * could not set them through `POST`/`PATCH /test-masters` even though this
     * is, per ADR-087 D-14, the ONE write path for `LabTest`. Deliberately
     * excluded from this set: `printName`, `testTypeCode`, `nameQualityStatus`
     * and anything else under "ADR-087 Phase 5 — clinical identity" in the
     * Prisma model — those are set ONLY by `ClinicalMappingService.approveMapping`
     * (the comment there: "the only path by which printName is ever set"), and
     * must stay that way. `disciplineId`/`departmentId`/`specimenTypeId`/
     * `containerId` here are the raw, unconfirmed classification a maker can
     * freely draft; clinical identity is proposed and approved separately.
     */
    disciplineId: zod_1.z.string().uuid().nullish(),
    departmentId: zod_1.z.string().uuid().nullish(),
    specimenTypeId: zod_1.z.string().uuid().nullish(),
    containerId: zod_1.z.string().uuid().nullish(),
    preferredVolume: zod_1.z.number().positive().nullish(),
    fastingRequired: zod_1.z.boolean().optional(),
    fastingHours: zod_1.z.number().int().positive().nullish(),
    /**
     * Admin full-edit pass (2026-09-22) — the rest of the Info tab's plain,
     * non-clinical-identity fields. `shortName`/`category` are unrestricted at
     * any status (not in `DRAFT_ONLY_TEST_MASTER_FIELDS`); the specimen/prep
     * fields here mirror `LabTest`'s own columns, distinct from the per-mapping
     * overrides on `LabTestSpecimen` (see the lab-test-specimen CRUD for those).
     *
     * Deliberately still excluded: `legacySourceName` ("Immutable. NEVER
     * printed." per the Prisma model's own doc comment — this is the verbatim
     * import-time value kept for traceability, not a workflow lock, so it stays
     * out regardless of the admin override), and everything already excluded
     * above (`printName`, `testTypeCode`, `nameQualityStatus`).
     */
    shortName: zod_1.z.string().max(60).nullish(),
    category: zod_1.z.string().max(100).nullish(),
    patientPreparation: zod_1.z.string().nullish(),
    specialCollectionInstruction: zod_1.z.string().nullish(),
    transportTemperature: zod_1.z.string().max(50).nullish(),
    transportTimeLimitMinutes: zod_1.z.number().int().positive().nullish(),
    stabilityPeriodHours: zod_1.z.number().int().positive().nullish(),
    /**
     * Admin Override (2026-09-22) — required, and only meaningful, when this
     * request touches a `DRAFT_ONLY_TEST_MASTER_FIELDS` entry on a test whose
     * `status` is no longer DRAFT. See
     * `TestMasterService.assertDraftOnlyFieldsAllowed`, which is where this is
     * actually enforced (conditionally, so it stays optional here in Zod).
     */
    overrideReason: zod_1.z.string().min(3).max(500).optional(),
};
exports.createTestMasterSchema = zod_1.z
    .object(exports.testMasterShape)
    .refine((value) => value.referenceRangeLow === undefined ||
    value.referenceRangeHigh === undefined ||
    value.referenceRangeLow <= value.referenceRangeHigh, { message: 'referenceRangeLow must be less than or equal to referenceRangeHigh', path: ['referenceRangeLow'] });
exports.updateTestMasterSchema = zod_1.z.object({
    name: exports.testMasterShape.name.optional(),
    sampleTypeId: exports.testMasterShape.sampleTypeId,
    methodId: exports.testMasterShape.methodId,
    unitId: exports.testMasterShape.unitId,
    referenceRangeLow: exports.testMasterShape.referenceRangeLow,
    referenceRangeHigh: exports.testMasterShape.referenceRangeHigh,
    referenceRangeText: exports.testMasterShape.referenceRangeText,
    turnaroundMinutes: exports.testMasterShape.turnaroundMinutes,
    chargeId: exports.testMasterShape.chargeId,
    disciplineId: exports.testMasterShape.disciplineId,
    departmentId: exports.testMasterShape.departmentId,
    specimenTypeId: exports.testMasterShape.specimenTypeId,
    containerId: exports.testMasterShape.containerId,
    preferredVolume: exports.testMasterShape.preferredVolume,
    fastingRequired: exports.testMasterShape.fastingRequired,
    fastingHours: exports.testMasterShape.fastingHours,
    shortName: exports.testMasterShape.shortName,
    category: exports.testMasterShape.category,
    patientPreparation: exports.testMasterShape.patientPreparation,
    specialCollectionInstruction: exports.testMasterShape.specialCollectionInstruction,
    transportTemperature: exports.testMasterShape.transportTemperature,
    transportTimeLimitMinutes: exports.testMasterShape.transportTimeLimitMinutes,
    stabilityPeriodHours: exports.testMasterShape.stabilityPeriodHours,
    overrideReason: exports.testMasterShape.overrideReason,
});
/** Fields gated to DRAFT-status tests only — see `TestMasterService.update`. */
exports.DRAFT_ONLY_TEST_MASTER_FIELDS = [
    'disciplineId',
    'departmentId',
    'specimenTypeId',
    'containerId',
    'preferredVolume',
    'fastingRequired',
    'fastingHours',
];
//# sourceMappingURL=test-master.schema.js.map